import {
  Directive,
  HostBinding,
  HostListener,
  Inject,
  Input,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { WindowRef } from '@x/common/browser';
import {
  CloudinaryUrl,
  Gravity,
  ImageCropMode,
  ImageTransformationOptions,
  constructCloudinaryUrl,
  deconstructCloudinaryUrl,
  isCloudinaryUrl,
  transformPathFromOptions,
} from './cloudinary';
import { CLOUDINARY_MODULE_CONFIG, CloudinaryModuleConfig } from './cloudinary.config';

@Directive({
  selector: '[xCloudImage]',
})
export class CloudImageDirective implements OnChanges {
  @Input('xCloudImage')
  set url(url: string | undefined) {
    this._url = url;
    if (url) {
      if (isCloudinaryUrl(url)) {
        this._parsed = deconstructCloudinaryUrl(url);
      } else {
        this._parsed = {
          cloud_name: this.config.cloudName,
          type: 'image',
          mode: 'fetch',
          public_id: url,
        };
      }
    }
  }
  get url() {
    return this._url;
  }

  @Input()
  crop?: ImageCropMode;

  @Input()
  @HostBinding('attr.width')
  width?: number | string;

  @Input()
  @HostBinding('attr.height')
  height?: number | string;

  @Input()
  size?: string;

  @Input()
  format?: string = 'auto';

  @Input()
  aspectRatio?: number | string;

  @Input()
  gravity?: Gravity;

  @Input()
  x?: number | string;

  @Input()
  y?: number | string;

  @Input()
  zoom?: number | string;

  @Input()
  effect?: string | Array<number | string>;

  @Input()
  background?: string;

  @Input()
  radius?: number | string;

  @Input()
  dpr?: number | string;

  @Input()
  quality?: number | string;

  @Input()
  startOffset?: number | 'auto';

  @Input()
  options?: ImageTransformationOptions;

  @Input()
  noImage: string | SafeUrl = this.sanitizer.bypassSecurityTrustUrl(
    'data:image/svg+xml;base64,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',
  );

  @HostBinding('srcset')
  @HostBinding('src')
  src?: string | SafeUrl;

  @HostBinding('class.loaded')
  isLoaded = false;

  _parsed?: CloudinaryUrl | null;
  _url?: string;

  constructor(
    private sanitizer: DomSanitizer,
    @Inject(CLOUDINARY_MODULE_CONFIG)
    readonly config: CloudinaryModuleConfig,
    private window: WindowRef,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (!this._parsed) {
      this.src = undefined;
    }

    const transform: ImageTransformationOptions = {
      crop: this.crop,
      width: this.width,
      height: this.height,
      size: this.size,
      aspect_ratio: this.aspectRatio,
      gravity: this.gravity,
      x: this.x,
      y: this.y,
      zoom: this.zoom,
      effect: this.effect,
      background: this.background,
      radius: this.radius,
      dpr: this.dpr ?? Math.min(2, Math.round(this.window.devicePixelRatio)),
      quality: this.quality,
      format: this.format,
      start_offset: this.startOffset,
      ...this.options,
    };

    const url = constructCloudinaryUrl({
      ...this._parsed,
      transform: transformPathFromOptions(transform),
    });

    if (this.src !== url) {
      this.isLoaded = false;
      this.src = url;
    }
  }

  @HostListener('load', ['$event'])
  onLoad(event: any) {
    this.isLoaded = true;
  }

  @HostListener('error', ['$event'])
  onError(event: any) {
    this.src = this.noImage;
  }
}
